/**
 * What a file is, as the composer, the transcript and the privacy panel all show it (TMMAPP-0142
 * items 25a–25c): its size, a PDF's pages or a sheet's rows when served, and whether the sheet was
 * cut to the bounds the assistant receives. Each fact is its own text, never concatenated copy.
 */
import { Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AssistantAttachmentKind } from '@/api/assistant';
import { DocumentFileIcon, ImageFileIcon, SheetFileIcon } from '@/components/icons';
import { formatByteSize } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

/** The kind's icon (a plain file for a kind this build doesn't know). */
export function AttachmentKindIcon({ kind }: { kind: AssistantAttachmentKind | null }) {
  if (kind === 'IMAGE') return <ImageFileIcon />;
  if (kind === 'SHEET') return <SheetFileIcon />;
  return <DocumentFileIcon />;
}

export function AttachmentFacts({
  sizeBytes,
  pages,
  rows,
  truncated,
}: {
  sizeBytes: number;
  pages: number | null;
  rows: number | null;
  truncated: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fact = { fontSize: 12, color: tokens.text2 } as const;
  return (
    <Stack direction="row" sx={{ columnGap: 1, flexWrap: 'wrap' }} data-attachment-facts="">
      <Typography component="span" sx={fact}>
        {formatByteSize(sizeBytes, locale)}
      </Typography>
      {pages !== null && (
        <Typography component="span" sx={fact}>
          {t('assistant.attach.pages', { count: pages })}
        </Typography>
      )}
      {rows !== null && (
        <Typography component="span" sx={fact}>
          {t('assistant.attach.rows', { count: rows })}
        </Typography>
      )}
      {truncated && (
        <Typography component="span" sx={fact}>
          {t('assistant.attach.truncated')}
        </Typography>
      )}
    </Stack>
  );
}
